import React, { useState, useMemo, useEffect } from "react";
import { Modal, Form, Input, Button, Typography, Result, Row, Col, Alert } from "antd";
import { LockOutlined } from "@ant-design/icons";
import logoImg from "../../assets/logo/dealVoice.png";
import { resetPasswordWithToken } from "../../../services/authService";

const { Title, Paragraph, Text } = Typography;

const REQUIREMENT_CONFIG = [
  { key: "length", label: "8+ characters" },
  { key: "upper", label: "Uppercase letter" },
  { key: "number", label: "Number" },
  { key: "symbol", label: "Symbol (!@#…)" },
];

const checkPassword = (password = "") => {
  const length = password.length >= 8;
  const upper = /[A-Z]/.test(password);
  const number = /\d/.test(password);
  const symbol = /[^A-Za-z\d]/.test(password);
  const isStrong = length && upper && number && symbol;
  return { length, upper, number, symbol, isStrong };
};

const PasswordChecklist = ({ checks }) => (
  <Row gutter={[8, 4]} style={{ marginTop: 8 }}>
    {REQUIREMENT_CONFIG.map(({ key, label }) => {
      const isValid = Boolean(checks[key]);
      return (
        <Col span={12} key={key}>
          <Text style={{ fontSize: 12, color: isValid ? "#10b981" : "#94a3b8" }}>
            {isValid ? "✓" : "○"} {label}
          </Text>
        </Col>
      );
    })}
  </Row>
);

const ResetSuccessView = ({ onLogin }) => (
  <Result
    status="success"
    title="Password Reset!"
    subTitle="Your password has been successfully updated."
    extra={[
      <Button type="primary" key="login" size="large" block onClick={onLogin}>
        Log In
      </Button>,
    ]}
  />
);

function ResetPwdModal({ isOpen, closeModals, openLogin, token }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const newPasswordValue = Form.useWatch("newPassword", form) || "";
  const pwdChecks = useMemo(() => checkPassword(newPasswordValue), [newPasswordValue]);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setError("");
      setSuccess(false);
    }
  }, [isOpen, form]);

  const onFinish = async (values) => {
    if (!pwdChecks.isStrong) return;
    setError("");
    setLoading(true);
    try {
      const activeToken = token || new URLSearchParams(window.location.search).get("resetToken") || "";
      if (!activeToken) {
        throw new Error("Password reset token is missing or expired. Please request a new link.");
      }
      await resetPasswordWithToken({
        token: activeToken,
        newPassword: values.newPassword,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Failed to update password. Token may have expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = () => {
    setSuccess(false);
    setError("");
    closeModals();
    openLogin();
  };

  return (
    <Modal open={isOpen} onCancel={closeModals} footer={null} centered width={440} styles={{ body: { padding: "2rem 1.5rem" } }}>
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <img src={logoImg} alt="DealVoice Logo" style={{ height: 36, objectFit: "contain" }} />
      </div>

      <Title level={3} style={{ textAlign: "center", margin: "0 0 0.5rem" }}>
        New password
      </Title>
      <Paragraph type="secondary" style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        Set a secure password for your account.
      </Paragraph>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {success ? (
        <ResetSuccessView onLogin={handleLoginClick} />
      ) : (
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "Please enter a new password" },
              {
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  const checks = checkPassword(value);
                  if (!checks.isStrong) {
                    return Promise.reject(new Error("Password does not satisfy all criteria below"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra={<PasswordChecklist checks={pwdChecks} />}
          >
            <Input.Password size="large" prefix={<LockOutlined style={{ color: "#94a3b8" }} />} placeholder="Min. 8 chars + A + 1 + !" />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password size="large" prefix={<LockOutlined style={{ color: "#94a3b8" }} />} placeholder="••••••••" />
          </Form.Item>

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              disabled={!pwdChecks.isStrong}
              style={{ height: 44, borderRadius: 12, fontWeight: 600 }}
            >
              Update password
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

export default ResetPwdModal;
